import { api } from '@goaltree/backend/convex/_generated/api'
import { withShareUtm } from '@goaltree/backend/convex/lib/shareUtm'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@goaltree/ui/components/sheet'
import { cn } from '@goaltree/ui/lib/utils'
import { useQuery } from 'convex/react'
import { Gift, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

const DISMISS_KEY = 'goaltree_invite_dismissed'
const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://goaltree.ai'

// "Give the gift of Goaltree" banner (modelled on Claude's send-invite card).
// Pressing the CTA opens a details sheet with the offer + a Share action; the
// invite count comes from the server (referrals redeemed against your code).
export function InviteBanner({ className }: { className?: string }) {
  const { t } = useTranslation()
  const [dismissed, setDismissed] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem(DISMISS_KEY) === '1',
  )
  const [sheetOpen, setSheetOpen] = useState(false)

  if (dismissed) return null

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, '1')
    setDismissed(true)
  }

  return (
    <>
      <div
        className={cn(
          'relative overflow-hidden rounded-xl border border-primary/30 bg-gradient-to-br from-primary/10 to-primary/[0.04] px-4 py-3',
          className,
        )}
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('inbox.invite.dismiss')}
          className="absolute right-2 top-2 flex h-6 w-6 cursor-pointer items-center justify-center rounded-md text-muted-foreground/50 transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary-text">
            <Gift className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-foreground">{t('inbox.invite.title')}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{t('inbox.invite.subtitle')}</p>
            <button
              type="button"
              onClick={() => setSheetOpen(true)}
              className="mt-2.5 flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-full bg-primary px-3.5 py-2 text-xs font-semibold text-primary-foreground transition-all hover:bg-primary/90 active:scale-[0.99]"
            >
              {t('inbox.invite.send')}
            </button>
          </div>
        </div>
      </div>

      <InviteSheet open={sheetOpen} onOpenChange={setSheetOpen} />
    </>
  )
}

export function InviteSheet({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const status = useQuery(api.referrals.getReferralStatus)
  const total = status?.total ?? 3
  const left = status?.remaining ?? total
  const referralUrl = status?.code ? `${SITE_URL}/referral/${status.code}` : `${SITE_URL}/referral`

  async function handleShare() {
    // Share the clean referral URL only (plus a short title). Passing a long
    // `text` causes some share targets to glue it onto the URL, mangling the
    // link — the offer copy already lives in the sheet the user is looking at.
    const shareData = {
      title: t('inbox.invite.title'),
      url: withShareUtm(referralUrl, 'invite_link'),
    }
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share(shareData)
      } else {
        await navigator.clipboard.writeText(withShareUtm(referralUrl, 'invite_link'))
        toast.success(t('inbox.invite.copied'))
      }
      onOpenChange(false)
    } catch {
      // Share sheet dismissed by the user — nothing to do.
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="mx-auto flex max-w-md flex-col items-center gap-3 rounded-t-2xl px-6 pb-[calc(env(safe-area-inset-bottom,0px)+2rem)] pt-8 text-center"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 text-primary-text">
          <Gift className="h-6 w-6" />
        </div>
        <SheetTitle className="text-lg font-black text-foreground">
          {t('inbox.invite.title')}
        </SheetTitle>
        <SheetDescription className="max-w-sm text-sm leading-relaxed text-muted-foreground">
          {t('inbox.invite.sheetDesc')}{' '}
          <a
            href={`${SITE_URL}/terms`}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-foreground"
          >
            {t('inbox.invite.terms')}
          </a>
        </SheetDescription>
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground/60">
          {t('inbox.invite.remaining', { left, total })}
        </p>
        <button
          type="button"
          onClick={handleShare}
          disabled={left === 0}
          className="mt-1 w-full cursor-pointer rounded-full bg-primary py-3 text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t('inbox.invite.share')}
        </button>
      </SheetContent>
    </Sheet>
  )
}
